/**
 * Frontend tool registry for the Kortex AI chat.
 *
 * Tools live on the global window.kortex API so any Vue component (or plain JS)
 * can register and clean up its own tools without importing this module.
 *
 * Usage from a component:
 *
 *   mounted: function () {
 *     window.kortex.registerTool(
 *       {
 *         type: 'function',
 *         function: {
 *           name: 'my_tool',
 *           description: '...',
 *           parameters: { type: 'object', properties: {} }
 *         }
 *       },
 *       function (args) { return { result: '...' } }
 *     )
 *   },
 *   beforeDestroy: function () {
 *     window.kortex.unregisterTool('my_tool')
 *   }
 *
 * window.kortex.tools is the live array of { schema, execute } entries.
 */

var DEFAULT_TOOL_POLICY = {
  category: 'write',
  risk: 'medium',
  requiresConfirmation: true,
  fullAccessAuto: false,
  label: ''
}

var TOOL_POLICY_BY_NAME = {
  get_current_page: { category: 'read', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  get_project_context: { category: 'read', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  reload_data: { category: 'ui', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  search_list: { category: 'ui', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  clear_search: { category: 'ui', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  sort_list: { category: 'ui', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  go_to_page: { category: 'ui', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  navigate_to_create: { category: 'ui', risk: 'low', requiresConfirmation: false, fullAccessAuto: true },
  run_function_test: { category: 'execute', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  run_ratchet_query: { category: 'execute', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  test_ratchet_connection: { category: 'execute', risk: 'medium', requiresConfirmation: true, fullAccessAuto: false },
  create_payment_link: { category: 'billing', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  publish_function_version: { category: 'publish', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  publish_forge_version: { category: 'publish', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  create_project: { category: 'destructive', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  clear_workflow_canvas: { category: 'destructive', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  remove_workflow_node: { category: 'destructive', risk: 'high', requiresConfirmation: true, fullAccessAuto: false },
  create_variant: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_html_file: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_md_file: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_widget: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_app: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_function: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_forge: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_ratchet_connection: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_project_settings: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true },
  save_workflow: { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true }
}

function cloneSchemaWithoutPolicy (schema) {
  var clean = Object.assign({}, schema || {})
  delete clean.kortex
  delete clean.x_kortex
  delete clean['x-kortex']
  if (clean.function) {
    clean.function = Object.assign({}, clean.function)
    delete clean.function.kortex
    delete clean.function.x_kortex
    delete clean.function['x-kortex']
  }
  return clean
}

function policyFromSchema (schema) {
  if (!schema) return null
  return schema.kortex || schema.x_kortex || schema['x-kortex'] ||
    (schema.function && (schema.function.kortex || schema.function.x_kortex || schema.function['x-kortex'])) ||
    null
}

function inferToolPolicy (name) {
  if (!name) return {}
  if (TOOL_POLICY_BY_NAME[name]) return TOOL_POLICY_BY_NAME[name]
  if (/^(get|list|find|search|format)_/.test(name) || /_info$|_status$/.test(name)) {
    return { category: 'read', risk: 'low', requiresConfirmation: false, fullAccessAuto: true }
  }
  if (/^(set|insert|replace|switch)_/.test(name) || /_content$|_code$/.test(name)) {
    return { category: 'draft', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true }
  }
  if (/^(save|refresh|add|connect)_/.test(name)) {
    return { category: 'persist', risk: 'medium', requiresConfirmation: true, fullAccessAuto: true }
  }
  if (/^(run|test|execute)_/.test(name)) {
    return { category: 'execute', risk: 'high', requiresConfirmation: true, fullAccessAuto: false }
  }
  if (/^(delete|remove|clear|archive|unarchive)_/.test(name)) {
    return { category: 'destructive', risk: 'high', requiresConfirmation: true, fullAccessAuto: false }
  }
  if (/^(create|publish|top_up|topup)_/.test(name) || name.indexOf('payment') !== -1) {
    return { category: 'high_risk', risk: 'high', requiresConfirmation: true, fullAccessAuto: false }
  }
  return {}
}

export function getToolPolicy (name, schema) {
  var explicit = policyFromSchema(schema)
  var inferred = inferToolPolicy(name)
  return Object.assign({}, DEFAULT_TOOL_POLICY, inferred, explicit || {}, { name: name || '' })
}

export function getToolCallPolicy (toolCall) {
  var name = toolCall && toolCall.function && toolCall.function.name
  var tools = window.kortex && window.kortex.tools ? window.kortex.tools : []
  for (var i = 0; i < tools.length; i++) {
    var schema = tools[i].schema
    if (schema && schema.function && schema.function.name === name) {
      return tools[i].policy || getToolPolicy(name, schema)
    }
  }
  return getToolPolicy(name, null)
}

export function shouldAutoExecuteTool (toolCall, accessMode, allowedToolNames) {
  var name = toolCall && toolCall.function && toolCall.function.name
  var policy = getToolCallPolicy(toolCall)
  if (accessMode === 'full') return true
  if (allowedToolNames && name && allowedToolNames.indexOf(name) !== -1) return true
  if (!policy.requiresConfirmation) return true
  return false
}

// Initialise the global API exactly once (guards against HMR double-init).
if (!window.kortex) {
  window.kortex = {
    tools: [],

    // Page context: free-form information the model receives about the current page.
    // Shape: { label: string, description: string, icon?: string }
    // Only one context is active at a time (the last one registered wins).
    _context: null,

    registerTool: function (schema, executeFn, policy) {
      var name = schema && schema.function && schema.function.name
      if (!name) { return }
      // Replace existing registration with the same name.
      window.kortex.unregisterTool(name)
      var cleanSchema = cloneSchemaWithoutPolicy(schema)
      window.kortex.tools.push({ schema: cleanSchema, execute: executeFn, policy: getToolPolicy(name, Object.assign({}, schema, { kortex: policy || policyFromSchema(schema) })) })
      window.dispatchEvent(new CustomEvent('kortex:capabilities-changed'))
    },

    unregisterTool: function (name) {
      var tools = window.kortex.tools
      for (var i = tools.length - 1; i >= 0; i--) {
        if (tools[i].schema && tools[i].schema.function && tools[i].schema.function.name === name) {
          tools.splice(i, 1)
        }
      }
      window.dispatchEvent(new CustomEvent('kortex:capabilities-changed'))
    },

    /**
     * Register page-level context for the model.
     * @param {string} label       - Short human-readable page name (e.g. "Document Editor")
     * @param {string} description - What the user is doing / what the model can help with
     * @param {string} [icon]      - Optional FontAwesome class, e.g. "fa-file-alt"
     */
    registerContext: function (label, description, icon) {
      window.kortex._context = { label: label, description: description, icon: icon || null }
      window.dispatchEvent(new CustomEvent('kortex:capabilities-changed'))
    },

    /**
     * Remove the current page context (call in beforeDestroy).
     */
    unregisterContext: function () {
      window.kortex._context = null
      window.dispatchEvent(new CustomEvent('kortex:capabilities-changed'))
    },

    // Helper indicator: when true the Kortex button in the header shows a glow/pulse
    // to hint the user that the AI can assist on the current page.
    _helperActive: false,

    /**
     * Activate the helper glow on the Kortex header button.
     * Call this from any component that registers page tools (e.g. in mounted()).
     */
    showHelper: function () {
      window.kortex._helperActive = true
      window.dispatchEvent(new CustomEvent('kortex:helper-changed'))
    },

    /**
     * Deactivate the helper glow (call in beforeDestroy).
     */
    hideHelper: function () {
      window.kortex._helperActive = false
      window.dispatchEvent(new CustomEvent('kortex:helper-changed'))
    }
  }
}

// Register the two built-in tools.
window.kortex.registerTool(
  {
    type: 'function',
    function: {
      name: 'get_current_page',
      description: 'Returns the current page URL and path the user is viewing in the application.',
      parameters: { type: 'object', properties: {} }
    }
  },
  function () {
    return {
      href: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    }
  }
)

window.kortex.registerTool(
  {
    type: 'function',
    function: {
      name: 'get_project_context',
      description: 'Returns the active Protokol project UUID, environment (live/dev/staging), and UI locale.',
      parameters: { type: 'object', properties: {} }
    }
  },
  function () {
    return {
      project_uuid: localStorage.getItem('current_project_uuid') || null,
      env: localStorage.getItem('current_env') || 'live',
      locale: localStorage.getItem('locale') || 'en_US'
    }
  }
)

/**
 * Returns the array of tool schemas to be sent to the backend on every message.
 * @returns {Array}
 */
export function getToolSchemas () {
  return window.kortex.tools.map(function (t) { return t.schema })
}

/**
 * Returns the current page context registered via window.kortex.registerContext, or null.
 * @returns {{ label: string, description: string, icon: string|null } | null}
 */
export function getPageContext () {
  return window.kortex._context || null
}

/**
 * Returns page-specific tool schemas — everything except the two built-in tools.
 * Used by the welcome screen to show what's available on the current page.
 * @returns {Array}
 */
export function getPageToolSchemas () {
  var builtIn = ['get_current_page', 'get_project_context']
  return window.kortex.tools
    .map(function (t) { return t.schema })
    .filter(function (s) { return s && s.function && builtIn.indexOf(s.function.name) === -1 })
}

/**
 * Executes a tool by name with the given arguments.
 * Always returns a Promise so async tool handlers (those that return a Promise)
 * are properly awaited before the result is sent back to the LLM.
 * @param {string} name
 * @param {Object} args
 * @returns {Promise<{ result: any } | { error: string }>}
 */
export function executeTool (name, args) {
  var tools = window.kortex.tools
  for (var i = 0; i < tools.length; i++) {
    if (tools[i].schema && tools[i].schema.function && tools[i].schema.function.name === name) {
      try {
        var r = tools[i].execute(args || {})
        if (r && typeof r.then === 'function') {
          return r
            .then(function (val) { return { result: val } })
            .catch(function (e) { return { error: String(e.message || e) } })
        }
        return Promise.resolve({ result: r })
      } catch (e) {
        return Promise.resolve({ error: String(e.message || e) })
      }
    }
  }
  return Promise.resolve({ error: 'Unknown tool: ' + name })
}
